import { create } from 'zustand';
import { createDefaultSection, duplicateBuilderSection, normalizeBuilderPageType, syncBuilderRecordWithDocument } from '@shared/builder/document';
import { validateBuilderDocument } from '@shared/builder/validation';
import type {
  BuilderDocument,
  BuilderDocumentRecord,
  BuilderPreviewDevice,
  BuilderSection,
  BuilderSectionType,
  ProjectDetailRecord
} from '@shared/types';

const AI_UNDO_HISTORY_LIMIT = 10;

interface AiUndoEntry {
  pageId: string;
  document: BuilderDocument;
}

function validateRecord(
  record: BuilderDocumentRecord,
  assetIds: string[]
): BuilderDocumentRecord {
  return {
    ...record,
    validation: validateBuilderDocument(record.document, {
      assetIds,
      requestedTarget: 'wordpress',
      now: new Date().toISOString()
    })
  };
}

function preserveValidation(record: BuilderDocumentRecord, document: BuilderDocument): BuilderDocumentRecord {
  return {
    ...syncBuilderRecordWithDocument(record, document),
    validation: record.validation
  };
}

function upsertDirtyPageId(current: string[], pageId: string): string[] {
  return current.includes(pageId) ? current : [...current, pageId];
}

function upsertPageId(current: string[], pageId: string): string[] {
  return current.includes(pageId) ? current : [...current, pageId];
}

function selectNextSection(record: BuilderDocumentRecord | null, preferredId: string | null): string | null {
  if (!record) {
    return null;
  }

  if (preferredId && record.document.sections.some((section) => section.id === preferredId)) {
    return preferredId;
  }

  return record.document.sections[0]?.id ?? null;
}

interface ProjectBuilderStore {
  projectId: string | null;
  assetIds: string[];
  pages: BuilderDocumentRecord[];
  selectedPageId: string | null;
  selectedSectionId: string | null;
  previewDevice: BuilderPreviewDevice;
  collapsedSectionIds: string[];
  isPickerOpen: boolean;
  isMaximized: boolean;
  isSaving: boolean;
  isDirty: boolean;
  dirtyPageIds: string[];
  staleValidationPageIds: string[];
  draggedSectionId: string | null;
  aiUndoStack: AiUndoEntry[];
  hydrate: (detail: ProjectDetailRecord) => void;
  selectPage: (pageId: string) => void;
  selectSection: (sectionId: string | null) => void;
  setPreviewDevice: (device: BuilderPreviewDevice) => void;
  toggleCollapsed: (sectionId: string) => void;
  openPicker: () => void;
  closePicker: () => void;
  toggleMaximized: () => void;
  setSaving: (value: boolean) => void;
  markPageSaved: (pageId: string) => void;
  validateStalePages: () => void;
  setDraggedSectionId: (sectionId: string | null) => void;
  setPageField: (field: 'title' | 'slug' | 'status' | 'seoTitle' | 'seoDescription' | 'pageType' | 'menuLocation', value: string) => void;
  setThemeTone: (value: BuilderDocumentRecord['document']['themeRefs']['tone']) => void;
  addSection: (type: BuilderSectionType) => void;
  duplicateSection: (sectionId: string) => void;
  deleteSection: (sectionId: string) => void;
  toggleSectionEnabled: (sectionId: string) => void;
  moveSection: (sectionId: string, direction: -1 | 1) => void;
  reorderSections: (dragSectionId: string, targetSectionId: string) => void;
  updateSection: (sectionId: string, updater: (section: any) => any) => void;
  applyAiDocument: (pageId: string, nextDocument: BuilderDocument) => void;
  applyAiSection: (pageId: string, sectionId: string, nextSection: BuilderSection) => void;
  undoAiChange: () => boolean;
  canUndoAi: () => boolean;
}

export const useProjectBuilderStore = create<ProjectBuilderStore>((set, get) => ({
  projectId: null,
  assetIds: [],
  pages: [],
  selectedPageId: null,
  selectedSectionId: null,
  previewDevice: 'desktop',
  collapsedSectionIds: [],
  isPickerOpen: false,
  isMaximized: false,
  isSaving: false,
  isDirty: false,
  dirtyPageIds: [],
  staleValidationPageIds: [],
  draggedSectionId: null,
  aiUndoStack: [],
  hydrate: (detail) =>
    set((state) => {
      const pages = detail.builderDocuments.map((record) =>
        validateRecord(structuredClone(record), detail.assets.map((asset) => asset.id))
      );
      const nextSelectedPageId =
        (state.projectId === detail.project.id && pages.some((record) => record.id === state.selectedPageId)
          ? state.selectedPageId
          : pages.find((record) => record.isPrimary)?.id) ?? pages[0]?.id ?? null;
      const activeRecord = pages.find((record) => record.id === nextSelectedPageId) ?? null;

      return {
        projectId: detail.project.id,
        assetIds: detail.assets.map((asset) => asset.id),
        pages,
        selectedPageId: nextSelectedPageId,
        selectedSectionId: selectNextSection(activeRecord, state.selectedSectionId),
        collapsedSectionIds: state.projectId === detail.project.id ? state.collapsedSectionIds : [],
        isPickerOpen: false,
        isMaximized: state.projectId === detail.project.id ? state.isMaximized : false,
        isSaving: false,
        isDirty: false,
        dirtyPageIds: [],
        staleValidationPageIds: [],
        draggedSectionId: null,
        aiUndoStack: state.projectId === detail.project.id ? state.aiUndoStack : []
      };
    }),
  selectPage: (selectedPageId) =>
    set((state) => {
      const nextPage = state.pages.find((record) => record.id === selectedPageId) ?? null;
      return {
        selectedPageId,
        selectedSectionId: selectNextSection(nextPage, null)
      };
    }),
  selectSection: (selectedSectionId) => set({ selectedSectionId }),
  setPreviewDevice: (previewDevice) => set({ previewDevice }),
  toggleCollapsed: (sectionId) =>
    set((state) => ({
      collapsedSectionIds: state.collapsedSectionIds.includes(sectionId)
        ? state.collapsedSectionIds.filter((entry) => entry !== sectionId)
        : [...state.collapsedSectionIds, sectionId]
    })),
  openPicker: () => set({ isPickerOpen: true }),
  closePicker: () => set({ isPickerOpen: false }),
  toggleMaximized: () => set((state) => ({ isMaximized: !state.isMaximized })),
  setSaving: (isSaving) => set({ isSaving }),
  markPageSaved: (pageId) =>
    set((state) => {
      const dirtyPageIds = state.dirtyPageIds.filter((entry) => entry !== pageId);
      return {
        dirtyPageIds,
        staleValidationPageIds: state.staleValidationPageIds.filter((entry) => entry !== pageId),
        isDirty: dirtyPageIds.length > 0
      };
    }),
  validateStalePages: () =>
    set((state) => {
      if (state.staleValidationPageIds.length === 0) {
        return state;
      }

      const stalePageIds = new Set(state.staleValidationPageIds);
      return {
        pages: state.pages.map((record) => (stalePageIds.has(record.id) ? validateRecord(record, state.assetIds) : record)),
        staleValidationPageIds: []
      };
    }),
  setDraggedSectionId: (draggedSectionId) => set({ draggedSectionId }),
  setPageField: (field, value) =>
    set((state) => {
      const activePage = state.pages.find((record) => record.id === state.selectedPageId);

      if (!activePage) {
        return state;
      }

      const nextDocument = structuredClone(activePage.document);

      if (field === 'title' || field === 'slug' || field === 'status' || field === 'seoTitle' || field === 'seoDescription' || field === 'pageType' || field === 'menuLocation') {
        if (field === 'pageType') {
          nextDocument.pageMeta.pageType = normalizeBuilderPageType(value, nextDocument.pageMeta.pageType);
        } else if (field === 'menuLocation') {
          nextDocument.pageMeta.menuLocation = value as BuilderDocumentRecord['document']['pageMeta']['menuLocation'];
        } else if (field === 'status') {
          nextDocument.pageMeta.status = value as BuilderDocumentRecord['status'];
        } else {
          nextDocument.pageMeta[field] = value;
        }
      }

      const nextRecord = preserveValidation(activePage, nextDocument);

      return {
        pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
        dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
        staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
        isDirty: true
      };
    }),
  setThemeTone: (value) =>
    set((state) => {
      const activePage = state.pages.find((record) => record.id === state.selectedPageId);

      if (!activePage) {
        return state;
      }

      const nextDocument = {
        ...activePage.document,
        themeRefs: {
          ...activePage.document.themeRefs,
          tone: value
        }
      };
      const nextRecord = preserveValidation(activePage, nextDocument);

      return {
        pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
        dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
        staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
        isDirty: true
      };
    }),
  addSection: (type) =>
    set((state) => {
      const activePage = state.pages.find((record) => record.id === state.selectedPageId);

      if (!activePage) {
        return state;
      }

      const nextSection = createDefaultSection(type);
      const nextDocument = {
        ...activePage.document,
        sections: [...activePage.document.sections, nextSection]
      };
      const nextRecord = preserveValidation(activePage, nextDocument);

      return {
        pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
        selectedSectionId: nextSection.id,
        isPickerOpen: false,
        dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
        staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
        isDirty: true
      };
    }),
  duplicateSection: (sectionId) =>
    set((state) => {
      const activePage = state.pages.find((record) => record.id === state.selectedPageId);

      if (!activePage) {
        return state;
      }

      const nextSections: BuilderSection[] = [];
      let duplicatedSectionId: string | null = null;

      for (const section of activePage.document.sections) {
        nextSections.push(section);
        if (section.id === sectionId) {
          const duplicate = duplicateBuilderSection(section);
          duplicatedSectionId = duplicate.id;
          nextSections.push(duplicate);
        }
      }

      const nextDocument = {
        ...activePage.document,
        sections: nextSections
      };
      const nextRecord = preserveValidation(activePage, nextDocument);

      return {
        pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
        selectedSectionId: duplicatedSectionId ?? state.selectedSectionId,
        dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
        staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
        isDirty: true
      };
    }),
  deleteSection: (sectionId) =>
    set((state) => {
      const activePage = state.pages.find((record) => record.id === state.selectedPageId);

      if (!activePage) {
        return state;
      }

      const nextSections = activePage.document.sections.filter((section) => section.id !== sectionId);
      const nextDocument = {
        ...activePage.document,
        sections: nextSections
      };
      const nextRecord = preserveValidation(activePage, nextDocument);

      return {
        pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
        selectedSectionId: nextSections[0]?.id ?? null,
        collapsedSectionIds: state.collapsedSectionIds.filter((entry) => entry !== sectionId),
        dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
        staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
        isDirty: true
      };
    }),
  toggleSectionEnabled: (sectionId) =>
    get().updateSection(sectionId, (section) => ({
      ...section,
      enabled: !section.enabled
    })),
  moveSection: (sectionId, direction) =>
    set((state) => {
      const activePage = state.pages.find((record) => record.id === state.selectedPageId);

      if (!activePage) {
        return state;
      }

      const currentIndex = activePage.document.sections.findIndex((section) => section.id === sectionId);
      const targetIndex = currentIndex + direction;

      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= activePage.document.sections.length) {
        return state;
      }

      const nextSections = [...activePage.document.sections];
      const [movedSection] = nextSections.splice(currentIndex, 1);
      if (!movedSection) {
        return state;
      }
      nextSections.splice(targetIndex, 0, movedSection);

      const nextDocument = {
        ...activePage.document,
        sections: nextSections
      };
      const nextRecord = preserveValidation(activePage, nextDocument);

      return {
        pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
        dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
        staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
        isDirty: true
      };
    }),
  reorderSections: (dragSectionId, targetSectionId) =>
    set((state) => {
      const activePage = state.pages.find((record) => record.id === state.selectedPageId);

      if (!activePage || dragSectionId === targetSectionId) {
        return { draggedSectionId: null };
      }

      const nextSections = [...activePage.document.sections];
      const dragIndex = nextSections.findIndex((section) => section.id === dragSectionId);
      const targetIndex = nextSections.findIndex((section) => section.id === targetSectionId);

      if (dragIndex < 0 || targetIndex < 0) {
        return { draggedSectionId: null };
      }

      const [draggedSection] = nextSections.splice(dragIndex, 1);
      if (!draggedSection) {
        return {
          draggedSectionId: null
        };
      }
      nextSections.splice(targetIndex, 0, draggedSection);

      const nextDocument = {
        ...activePage.document,
        sections: nextSections
      };
      const nextRecord = preserveValidation(activePage, nextDocument);

      return {
        pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
        dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
        staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
        isDirty: true,
        draggedSectionId: null
      };
    }),
  updateSection: (sectionId, updater) =>
    set((state) => {
      const activePage = state.pages.find((record) => record.id === state.selectedPageId);

      if (!activePage) {
        return state;
      }

      const nextDocument = {
        ...activePage.document,
        sections: activePage.document.sections.map((section) => (section.id === sectionId ? updater(section) : section))
      };
      const nextRecord = preserveValidation(activePage, nextDocument);

      return {
        pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
        dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
        staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
        isDirty: true
      };
    }),
  applyAiDocument: (pageId, nextDocument) =>
    set((state) => {
      const targetPage = state.pages.find((record) => record.id === pageId);

      if (!targetPage) {
        return state;
      }

      const undoEntry: AiUndoEntry = {
        pageId,
        document: structuredClone(targetPage.document)
      };
      const nextUndoStack = [...state.aiUndoStack, undoEntry].slice(-AI_UNDO_HISTORY_LIMIT);

      const nextRecord = preserveValidation(targetPage, structuredClone(nextDocument));

      return {
        pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
        selectedPageId: pageId,
        selectedSectionId: selectNextSection(nextRecord, state.selectedSectionId),
        dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
        staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
        isDirty: true,
        aiUndoStack: nextUndoStack
      };
    }),
  applyAiSection: (pageId, sectionId, nextSection) =>
    set((state) => {
      const targetPage = state.pages.find((record) => record.id === pageId);

      if (!targetPage) {
        return state;
      }

      const targetIndex = targetPage.document.sections.findIndex((section) => section.id === sectionId);

      if (targetIndex < 0) {
        return state;
      }

      const undoEntry: AiUndoEntry = {
        pageId,
        document: structuredClone(targetPage.document)
      };
      const nextUndoStack = [...state.aiUndoStack, undoEntry].slice(-AI_UNDO_HISTORY_LIMIT);

      const replacement = { ...structuredClone(nextSection), id: sectionId } as BuilderSection;
      const nextSections = targetPage.document.sections.map((section) => (section.id === sectionId ? replacement : section));

      const nextDocument = {
        ...targetPage.document,
        sections: nextSections
      };
      const nextRecord = preserveValidation(targetPage, nextDocument);

      return {
        pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
        selectedPageId: pageId,
        selectedSectionId: sectionId,
        dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
        staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
        isDirty: true,
        aiUndoStack: nextUndoStack
      };
    }),
  undoAiChange: () => {
    const state = get();
    const entry = state.aiUndoStack[state.aiUndoStack.length - 1];

    if (!entry) {
      return false;
    }

    const targetPage = state.pages.find((record) => record.id === entry.pageId);

    if (!targetPage) {
      set({ aiUndoStack: state.aiUndoStack.slice(0, -1) });
      return false;
    }

    const nextRecord = preserveValidation(targetPage, structuredClone(entry.document));

    set({
      pages: state.pages.map((record) => (record.id === nextRecord.id ? nextRecord : record)),
      selectedPageId: entry.pageId,
      selectedSectionId: selectNextSection(nextRecord, state.selectedSectionId),
      dirtyPageIds: upsertDirtyPageId(state.dirtyPageIds, nextRecord.id),
      staleValidationPageIds: upsertPageId(state.staleValidationPageIds, nextRecord.id),
      isDirty: true,
      aiUndoStack: state.aiUndoStack.slice(0, -1)
    });

    return true;
  },
  canUndoAi: () => get().aiUndoStack.length > 0
}));
